import React, { Suspense, lazy } from 'react';
import { Container, Typography, Box, Stack, Chip, Paper } from '@mui/material';
import { Home2 } from './Home2';
import { Type } from './Type';
import { WaveEmoji } from './components';
import { CodingHighlights } from './CodingHighlights';

const Laptop3D = lazy(() =>
  import('./Laptop3D').then(module => ({ default: module.Laptop3D }))
);

export const Home = (): React.ReactNode => {
  const signalBadges = [
    { label: 'Shipping production systems', color: '#4ade80' },
    { label: 'Remote-friendly', color: '#22d3ee' },
    { label: 'Loves pairing & reviews', color: '#a855f7' },
  ];

  const quickFacts = [
    'Full-stack engineer crafting resilient experiences.',
    'I keep pipelines green, metrics visible, and UX sharp.',
    'Currently prototyping AI-powered developer tooling.',
  ];

  return (
    <Box
      component='section'
      sx={{
        position: 'relative',
        backgroundPosition: 'top center',
        backgroundRepeat: 'no-repeat',
        paddingBottom: '40px',
        paddingTop: '40px',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(circle at 20% 30%, rgba(34,211,238,0.12), transparent 32%), radial-gradient(circle at 80% 10%, rgba(74,222,128,0.15), transparent 30%)',
          opacity: 0.8,
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Container maxWidth='lg'>
          <Box
            sx={{
              padding: { xs: '5rem 0 2rem', md: '9rem 0 3rem' },
              color: 'whitesmoke',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Box sx={{ flex: { md: '0 0 58%' }, width: '100%' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 4 },
                    background:
                      'linear-gradient(135deg, rgba(8, 47, 73, 0.8), rgba(4, 120, 87, 0.4))',
                    border: '1px solid rgba(34, 211, 238, 0.25)',
                    borderRadius: 4,
                    boxShadow:
                      '0 0 0 1px rgba(34, 211, 238, 0.25), 0 24px 60px -24px rgba(34, 211, 238, 0.45)',
                    backdropFilter: 'blur(6px)',
                    position: 'relative',
                    overflow: 'hidden',
                    isolation: 'isolate',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background:
                        'radial-gradient(circle at 30% 20%, rgba(74,222,128,0.18), transparent 40%)',
                      opacity: 0.9,
                      pointerEvents: 'none',
                    },
                  }}
                >
                  <Typography
                    variant='h1'
                    sx={{
                      pb: 1,
                      fontSize: { xs: '1.9rem', md: '2.6rem' },
                      fontWeight: 800,
                      letterSpacing: '0.04em',
                    }}
                  >
                    Hello there <WaveEmoji>👋🏻</WaveEmoji>
                  </Typography>

                  <Typography
                    variant='h2'
                    sx={{
                      fontSize: { xs: '1.4rem', md: '2.2rem' },
                      fontWeight: 700,
                      mb: 2,
                      color: '#4ade80',
                    }}
                  >
                    Islom Mashanlo — building reliable software
                  </Typography>

                  <Box
                    sx={{
                      textAlign: { xs: 'center', md: 'left' },
                      minHeight: '90px',
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: { xs: '1.25rem', md: '1.8rem' },
                        color: '#22d3ee',
                        fontWeight: 700,
                        textShadow: '0 0 18px rgba(34,211,238,0.6)',
                        width: '100%',
                        display: 'flex',
                        justifyContent: { xs: 'center', md: 'flex-start' },
                      }}
                    >
                      <Type />
                    </Box>
                  </Box>

                  <Stack
                    direction='row'
                    spacing={1}
                    flexWrap='wrap'
                    useFlexGap
                    sx={{ mb: 3 }}
                  >
                    {signalBadges.map(badge => (
                      <Chip
                        key={badge.label}
                        label={badge.label}
                        sx={{
                          backgroundColor: 'rgba(2, 6, 23, 0.6)',
                          border: '1px solid rgba(148, 163, 184, 0.2)',
                          color: badge.color,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      />
                    ))}
                  </Stack>

                  <Stack spacing={1.5} sx={{ mb: 1 }}>
                    {quickFacts.map(line => (
                      <Typography
                        key={line}
                        variant='body1'
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          color: 'rgba(226,232,240,0.9)',
                        }}
                      >
                        <Box
                          component='span'
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: '#22d3ee',
                            boxShadow: '0 0 12px rgba(34,211,238,0.6)',
                          }}
                        />
                        {line}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              </Box>

              <Box sx={{ flex: { md: '0 0 42%' }, width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    pb: { xs: 2, md: 0 },
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      width: 340,
                      height: 340,
                      borderRadius: '50%',
                      background:
                        'radial-gradient(circle, rgba(34,211,238,0.18), rgba(2,6,23,0.1))',
                      filter: 'blur(12px)',
                      top: { xs: 30, md: 60 },
                      zIndex: 0,
                    }}
                  />
                  <Suspense
                    fallback={
                      <Box
                        sx={{
                          width: '100%',
                          height: 400,
                          background:
                            'linear-gradient(135deg, rgba(34,211,238,0.12), rgba(74,222,128,0.16))',
                          borderRadius: 3,
                          border: '1px solid rgba(34,211,238,0.25)',
                        }}
                      />
                    }
                  >
                    <Laptop3D />
                  </Suspense>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      <Home2 />
      <CodingHighlights />
    </Box>
  );
};
